import { AnnualFinalsLevel, AnnualFinalsSeasonLeague, AnnualFinalsSeasonScore } from "../../store/annual-finals/models";

// Zawodniczka może w jednym sezonie grać równolegle w lidze kobiet i w lidze open, więc trafia do dwóch
// klasyfikacji rocznych. W każdej z nich liczymy tylko punkty z lig tej kategorii - API zwraca w polach
// sezonu sumę wszystkich lig, a rozbicie w tablicy `leagues`.

const WOMEN_LEVEL_PREFIX = "women-";

export const isWomenLevel = (levelId: string) => levelId.indexOf(WOMEN_LEVEL_PREFIX) === 0;

export interface AnnualFinalsLevelSeasonScore {
    seasonNo: number;
    played: boolean;
    // Zawodniczka grała w tym sezonie, ale tylko w drugiej kategorii (nie liczymy tych punktów tutaj).
    playedOnlyInOtherCategory: boolean;
    // Ligi z drugiej kategorii pominięte w tej klasyfikacji.
    otherCategoryLeagues: AnnualFinalsSeasonLeague[];
    levelId: string;
    levelName: string;
    playedInHigherLevel: boolean;
    regularPoints: number;
    regularPointsCounted: number;
    playoffPoints: number;
    seasonPoints: number;
    rank: number;
    playoffRank: number | null;
    isSeasonWinner: boolean;
}

export interface AnnualFinalsLevelStanding {
    seed: number;
    playerId: string;
    name: string;
    surname: string;
    isSeasonWinner: boolean;
    wonSeasons: number[];
    advances: boolean;
    hasBye: boolean;
    totalPoints: number;
    seasons: AnnualFinalsLevelSeasonScore[];
}

export interface AnnualFinalsLevelView {
    id: string;
    name: string;
    advancingPlayersCount: number;
    byeSeedsCount: number;
    standings: AnnualFinalsLevelStanding[];
}

const notPlayed = (seasonNo: number, playedOnlyInOtherCategory: boolean, otherCategoryLeagues: AnnualFinalsSeasonLeague[]): AnnualFinalsLevelSeasonScore => ({
    seasonNo,
    played: false,
    playedOnlyInOtherCategory,
    otherCategoryLeagues,
    levelId: "",
    levelName: "",
    playedInHigherLevel: false,
    regularPoints: 0,
    regularPointsCounted: 0,
    playoffPoints: 0,
    seasonPoints: 0,
    rank: 0,
    playoffRank: null,
    isSeasonWinner: false
});

const played = (
    seasonNo: number,
    source: AnnualFinalsSeasonLeague | AnnualFinalsSeasonScore,
    playedInHigherLevel: boolean,
    otherCategoryLeagues: AnnualFinalsSeasonLeague[]
): AnnualFinalsLevelSeasonScore => ({
    seasonNo,
    played: true,
    playedOnlyInOtherCategory: false,
    otherCategoryLeagues,
    levelId: source.levelId,
    levelName: source.levelName,
    playedInHigherLevel,
    regularPoints: source.regularPoints,
    regularPointsCounted: source.regularPointsCounted,
    playoffPoints: source.playoffPoints,
    seasonPoints: source.seasonPoints,
    rank: source.rank,
    playoffRank: source.playoffRank,
    isSeasonWinner: source.isSeasonWinner
});

const projectScore = (score: AnnualFinalsSeasonScore, levelId: string): AnnualFinalsLevelSeasonScore => {

    if (!score.played) {
        return notPlayed(score.seasonNo, false, []);
    }

    const leagues = score.leagues || [];

    // Starsze wersje API nie zwracają rozbicia na ligi - wtedy pola sezonu są jedynym, co mamy.
    if (leagues.length === 0) {
        return played(score.seasonNo, score, score.playedInHigherLevel, []);
    }

    const women = isWomenLevel(levelId);
    const league = leagues.filter(l => isWomenLevel(l.levelId) === women)[0];
    const otherCategoryLeagues = leagues.filter(l => isWomenLevel(l.levelId) !== women);

    if (!league) {
        return notPlayed(score.seasonNo, true, otherCategoryLeagues);
    }

    // Punkty zbierane są w najniższej grupie danej kategorii, więc każda inna grupa jest grupą wyższą.
    return played(score.seasonNo, league, league.levelId !== levelId, otherCategoryLeagues);
};

export const buildLevelView = (level: AnnualFinalsLevel): AnnualFinalsLevelView => {

    const projected = (level.standings || []).map((standing, index) => {

        const seasons = standing.seasons.map(score => projectScore(score, level.id));

        return {
            standing,
            index,
            seasons,
            totalPoints: seasons.reduce((sum, score) => sum + score.seasonPoints, 0),
            wonSeasons: seasons.filter(score => score.isSeasonWinner).map(score => score.seasonNo)
        };
    });

    projected.sort((a, b) => {
        // Zwycięzcy sezonów awansują automatycznie, więc stoją na początku tabeli niezależnie od punktów.
        const aIsWinner = a.wonSeasons.length > 0;
        const bIsWinner = b.wonSeasons.length > 0;

        if (aIsWinner !== bIsWinner) {
            return aIsWinner ? -1 : 1;
        }

        if (a.totalPoints !== b.totalPoints) {
            return b.totalPoints - a.totalPoints;
        }

        // Przy równej liczbie punktów zostawiamy kolejność z API.
        return a.index - b.index;
    });

    return {
        id: level.id,
        name: level.name,
        advancingPlayersCount: level.advancingPlayersCount,
        byeSeedsCount: level.byeSeedsCount,
        standings: projected.map((entry, position) => ({
            seed: position + 1,
            playerId: entry.standing.playerId,
            name: entry.standing.name,
            surname: entry.standing.surname,
            isSeasonWinner: entry.wonSeasons.length > 0,
            wonSeasons: entry.wonSeasons,
            advances: position < level.advancingPlayersCount,
            hasBye: position < level.byeSeedsCount,
            totalPoints: entry.totalPoints,
            seasons: entry.seasons
        }))
    };
};
